import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';

import { Nullish } from '@plugins/shared/types';
import { typedKeys } from '@plugins/shared/utils/typedKeys';

import {
  ALL_COLORS,
  DEFAULT_CONTRAST_VALUES,
  SIZE_CLASSES,
} from '../../private/shared-button/constants';
import {
  DEFAULT_SIZE,
  DEFAULT_VARIANT,
} from '../../private/shared-button/defaults';
import {
  handleAriaChanges,
  handleButtonConfigurationChanges,
} from '../../private/shared-button/utils';

import { ButtonInjectionTokenService } from './button.injection-token.service';
import {
  AriaInput,
  ColorAccessibility,
  DisplayConfiguration,
  DisplayVariants,
  SizeClasses,
} from './types';

@Component({
  selector: 'plugins-shared-button',
  imports: [CommonModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  providers: [ButtonInjectionTokenService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedButtonComponent {
  readonly element = viewChild.required<ElementRef<HTMLElement>>('element');
  /** Set minumum contrast level for text and decorative elements */
  readonly colorAccessibility = input<ColorAccessibility | undefined>();

  /** Style the component by directly specifying the desired class.
   * When using this input, do not use the `variant`, `size` or `round`, as
   * these will overwrite the class. */
  readonly className = input<string | undefined>();

  readonly type = input<'button' | 'submit' | 'reset'>('button');

  /** Style with by specifying button variant, such as 'primary'
   * of 'secondary-outline' */
  readonly variant = input<DisplayVariants | undefined>();

  readonly size = input<SizeClasses | undefined>();
  readonly round = input<boolean | undefined>();
  readonly disabled = input(false);
  readonly buttonId = input<string | null, string | Nullish>(null, {
    transform: (val: string | Nullish) => val ?? null,
  });
  readonly aria = input<AriaInput>();
  /** Will overwrite the ariaLabel property in the `aria` Input. */
  readonly ariaLabel = input<string | undefined>();

  /** Directly set styles on the button element. For setting CSS variables, use `config` input. */
  readonly buttonStyle = input<Record<string, string> | undefined>();
  /** Overwrite CSS variables which determine the display of different variants */
  readonly config = input<DisplayConfiguration | undefined>();

  /** Specify the color for the displayed button, regardless of the current settings. */
  readonly color = input<string | undefined>();

  readonly clicked = output<MouseEvent>();
  readonly mouseEntered = output<Event>();
  readonly mouseLeft = output<Event>();
  readonly focused = output<Event>();
  readonly blurred = output<Event>();

  // DI
  private host = inject(ElementRef) as ElementRef<HTMLElement>;
  private renderer = inject(Renderer2);
  private tokenService = inject(ButtonInjectionTokenService);

  private readonly defaultVariant = DEFAULT_VARIANT;
  private readonly defaultSize: SizeClasses = DEFAULT_SIZE;
  /** Currently applied custom css. After input changes, used for comparison
   * to ensure unused properties are deleted. */
  #customCssProps: string[] = [];

  constructor() {
    effect(() => {
      this.#adjustStyles(
        handleButtonConfigurationChanges({
          color: this.color(),
          config: this.config(),
          currentVariant: this.currentVariant,
          injectedTheme: this.tokenService.injectedTheme ?? {},
          colorAccessibility: this.colorAccessibilitySetting,
        }),
      );
    });
  }

  // SIGNALS
  readonly colorAccessibilitySetting = computed(() => {
    const input = this.colorAccessibility();
    if (input) return input;
    const injectedToken = this.tokenService.injectedColorAccessibility;
    if (injectedToken) return injectedToken;
    return DEFAULT_CONTRAST_VALUES;
  });
  readonly #ariaCurrent = linkedSignal<AriaInput | undefined, AriaInput>({
    source: this.aria,
    computation: (currentValue, previousValue) =>
      handleAriaChanges({
        previousValue: previousValue?.value,
        currentValue,
        nativeButton: this.nativeElement,
        renderer: this.renderer,
      }),
  });

  /** Determine final button class when either a `className` or one of
   * the styling inputs (`variant | round | size`) changes.
   *
   * Styling inputs have priority over the `className` input. If any of the
   * styling inputs are truthy, `className` will be ignored and
   * an appropriate class combination wil be composed. */
  protected readonly buttonClass = computed((): string => {
    const defaultClass = [this.defaultVariant, this.defaultSize].join(' ');
    const className = this.className();
    // If at least one style is defined
    if ([this.variant(), this.size(), this.round()].some((val) => !!val)) {
      return [
        this.variant() ? this.variant() : this.defaultVariant,
        this.size() ? this.size() : this.defaultSize,
        this.round() ? 'round' : undefined,
      ]
        .filter(Boolean)
        .join(' ');
    }
    return className ? className : defaultClass;
  });
  protected readonly contentClass = computed(() =>
    this.buttonClass().split(' ').includes('round')
      ? 'content-center'
      : 'content-text',
  );
  protected readonly label = computed<string | null>(() => {
    const aria = this.#ariaCurrent();
    const ariaLabel = this.ariaLabel();
    return ariaLabel ?? aria?.ariaLabel ?? null;
  });
  protected readonly styles = computed(() => {});

  get nativeElement(): HTMLElement {
    return this.element().nativeElement;
  }

  get hostStyle(): CSSStyleDeclaration {
    return this.host.nativeElement.style;
  }

  get currentVariant(): DisplayVariants | null {
    const targetClasses = this.buttonClass().split(' ');
    if (!targetClasses.length) return null;
    const listWithoutSize = targetClasses.filter(
      (className) => !SIZE_CLASSES.some((size) => size === className),
    );
    return listWithoutSize.find((targetClass) =>
      ALL_COLORS.some((className) => className === targetClass),
    ) as DisplayVariants | null;
  }

  handleFocus(event: Event): void {
    this.focused.emit(event);
  }

  handleBlur(event: Event): void {
    this.blurred.emit(event);
  }

  #adjustStyles(styles: Record<string, string>): void {
    this.#customCssProps.forEach((prop) => this.hostStyle.removeProperty(prop));
    this.#customCssProps = typedKeys(styles);
    this.#customCssProps.forEach((key) =>
      this.hostStyle.setProperty(key, styles[key]),
    );
  }
}
